import React, { useState } from 'react';
import styled from 'styled-components';
import { Background, Border, Text } from 'styles/themeColors';
import { FontSize } from 'styles/themeSizes';
import FlexboxContainer from '../FlexboxContainer';
import { Space } from 'styles/themeSpaces';
import { BorderRadius } from 'styles/themeBorderRadius';
import { TableColumn } from './GenericTable';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import Checkbox from '@mui/material/Checkbox';

const Row = styled.div`
  transition: all 300ms ease 0s;
  font-size: ${FontSize.subMedium};
  line-height: 20px;
  vertical-align: top;
  display: flex;
  flex-direction: row;
  cursor: pointer;
  width: 100%;
`;

const RowWrapper = styled(FlexboxContainer)`
  background-color: ${Background.white};
  border-bottom: 1px solid ${Border.light};
  width: 100%;
  min-width: 100%;

  &:nth-child(2n + 1) {
    background-color: ${Background.light};
  }

  &:last-child {
    border-radius: 0 0 ${BorderRadius.m} ${BorderRadius.m};
  }
`;

const Cell = styled.div<{ width?: string; cellStyle?: any; isFlexible?: boolean; headerStyle?: any }>`
  padding: ${Space.sp2x} ${Space.spBase} ${Space.sp2x} ${Space.sp2x};
  font-weight: 400;
  font-size: 16px;
  color: ${Text.primary};
  white-space: normal;
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  text-align: left;
  
  ${props => props.isFlexible
    ? `flex: 1; min-width: 120px;`
    : `width: ${props.width || 'auto'}; min-width: ${props.width || '120px'};`
  }
  
  ${props => props.cellStyle && Object.entries(props.cellStyle).map(([key, value]) =>
    `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value};`
  ).join(' ')}
  
  ${props => props.headerStyle && Object.entries(props.headerStyle).map(([key, value]) =>
    key !== 'color' && key !== 'fontWeight' ? `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value};` : ''
  ).join(' ')}
`;

const ExpandCell = styled(Cell)`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px !important;
  min-width: 40px !important;
`;

const DetailPanelContainer = styled.div`
  background-color: ${Background.white};
  border-left: 1px solid ${Border.light};
  border-right: 1px solid ${Border.light};
  border-bottom: 1px solid ${Border.light};
  padding: ${Space.spBase};
`;

const AfterRowContainer = styled.div`
  position: sticky;
  left: 0;
  width: 100cqw;
  max-width: 100%;
  box-sizing: border-box;
  padding: 0 ${Space.sp2x} ${Space.sp2x};
`;

interface Props {
  rowData: any;
  columns: TableColumn[];
  onRowClick?: (event: React.MouseEvent, rowData: any) => void;
  detailPanel?: any[];
  enableSelection?: boolean;
  isSelected?: boolean;
  onSelect?: (rowData: any) => void;
  isRowDisabled?: boolean;
}

const GenericTableRow = ({ rowData, columns, onRowClick, detailPanel, enableSelection, isSelected, onSelect, isRowDisabled }: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleRowClick = () => {
    if (onRowClick) {
      onRowClick({} as React.MouseEvent, rowData);
    }
  };

  const handleExpandClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  // Helper function to get nested field values
  const getFieldValue = (data: any, field: string) => {
    const fields = field.split('.');
    let value = data;
    for (const f of fields) {
      value = value?.[f];
      if (value === undefined || value === null) {
        return null;
      }
    }
    return value;
  };

  // Same logic as header - determine which columns should have fixed width vs flexible width
  const getColumnProps = (column: TableColumn) => {
    const title = column.title;
    
    // Orders table specific fixed widths
    const orderFixedWidths: Record<string, string> = {
      '#': '150px',
      'Status': '120px',
      'Date': '140px',
      'Payment': '120px',
      'Total amount': '140px'
    };

    // Check if this is an orders table column with specific width
    if (orderFixedWidths[title]) {
      return {
        width: orderFixedWidths[title],
        isFlexible: false
      };
    }

    // For tables, use column width if specified
    if (column.width && column.width !== 'auto') {
      // For percentage widths, make them flexible but constrained
      if (typeof column.width === 'string' && column.width.includes('%')) {
        return {
          width: column.width,
          isFlexible: true  // Changed to true for better responsiveness
        };
      }
      return {
        width: column.width,
        isFlexible: false
      };
    }

    // Default: flexible columns that will share remaining space
    return {
      width: 'auto',
      isFlexible: true
    };
  };

  // Check if this row has detail panel content
  const hasDetailPanel = detailPanel && detailPanel.length > 0;
  const detailPanelContent = hasDetailPanel ? detailPanel[0].render(rowData) : null;
  const shouldShowDetailPanel = hasDetailPanel && detailPanelContent;

  return (
    <RowWrapper flexDirection="column">
      <Row onClick={handleRowClick}>
        {/* Selection checkbox */}
        {enableSelection && (
          <ExpandCell onClick={(e) => { e.stopPropagation(); if (!isRowDisabled) onSelect?.(rowData); }}>
            <Checkbox
              checked={isSelected}
              disabled={isRowDisabled}
              size="medium"
              onClick={(e) => e.stopPropagation()}
              onChange={() => { if (!isRowDisabled) onSelect?.(rowData); }}
              style={{marginLeft: 23}}
            />
          </ExpandCell>
        )}

        {/* Expand/Collapse button */}
        {shouldShowDetailPanel && (
          <ExpandCell onClick={handleExpandClick}>
            {isExpanded ? <ExpandLess style={{ alignSelf: 'flex-start' }} /> : <ExpandMore style={{ alignSelf: 'flex-start' }} />}
          </ExpandCell>
        )}
        {!shouldShowDetailPanel && hasDetailPanel && (
          <ExpandCell />
        )}

        {/* Regular cells */}
        {columns.map((column, index) => {
          const { width, isFlexible } = getColumnProps(column);
          const fieldValue = column.field ? getFieldValue(rowData, column.field) : null;
          
          // Handle cellStyle as function or object
          let cellStyle = {};
          if (typeof column.cellStyle === 'function') {
            cellStyle = column.cellStyle(fieldValue, rowData) || {};
          } else if (column.cellStyle) {
            cellStyle = column.cellStyle;
          }
          
          // Use render function if available, otherwise show fieldValue or empty string
          let displayValue;
          if (column.render) {
            displayValue = column.render(rowData);
          } else {
            displayValue = fieldValue !== null && fieldValue !== undefined ? fieldValue : '';
          }
          
          return (
            <Cell
              key={index}
              width={width}
              isFlexible={isFlexible}
              cellStyle={cellStyle}
              headerStyle={column.headerStyle}
            >
              {displayValue}
            </Cell>
          );
        })}
      </Row>

      {/* Always-visible full-width content rendered after the row */}
      {rowData.afterRowContent && (
        <AfterRowContainer>
          {rowData.afterRowContent}
        </AfterRowContainer>
      )}

      {/* Detail panel */}
      {shouldShowDetailPanel && isExpanded && (
        <DetailPanelContainer>
          {detailPanelContent}
        </DetailPanelContainer>
      )}
    </RowWrapper>
  );
};

export default GenericTableRow;